import {
  AssetRoutineState,
  FeatureName,
  useFindByQuery,
  useGrippContext,
  useIsFeatureEnabled,
  WorkspaceRoutine,
} from '@gripp/shared-logic';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { LayoutChangeEvent, ScrollView, StyleSheet, View } from 'react-native';
import { extractAndDeduplicateRoutines } from '../../utils/assetHelpers';
import { RoutineModules } from '../routine';
import { SubHeader, SubHeaderProps } from './headerTypes';
import { SubHeaderText } from './subHeaderText';

interface SortedRoutineState {
  state?: AssetRoutineState<any>;
  config: any;
  order?: number;
  type: any;
  routines?: WorkspaceRoutine[];
}

// Horizontal gap between pills (matches the per-pill marginRight below).
const PILL_GAP = 8;
// Width reserved for the "+N" overflow pill when collapsing. A neutral
// StatusTag with a 1-2 digit count; a little generous so "+N" never clips.
const OVERFLOW_PILL_WIDTH = 48;

export const RoutineSubHeader: SubHeader = (props: SubHeaderProps) => {
  const { logger } = useGrippContext();
  const { workspaceAsset } = props;
  const [sorted, setSorted] = useState<SortedRoutineState[] | undefined>();
  const { data } = useFindByQuery({
    modelName: 'WorkspaceRoutineVersion',
    query: GET_ROUTINE_VERSIONS,
    variables: {
      filter: {
        id: {
          in: [
            ...(workspaceAsset.routineState?.map(
              (x) => x.workspaceRoutineVersionId
            ) || []),
            ...(workspaceAsset.routines
              ?.filter(Boolean)
              ?.map((r) => r.currentVersion?.id)
              .filter(Boolean) || []),
          ],
        },
      },
    },
  });
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  useEffect(() => {
    if (!data) return;

    const directRoutines =
      workspaceAsset.routines?.filter((r) => r?.deactivated !== true) || [];
    const externalRoutines = extractAndDeduplicateRoutines(workspaceAsset);
    const allRoutines = [...directRoutines, ...externalRoutines];
    const activeRoutineIds = new Set(allRoutines.map((r) => r.id));

    const sortedRoutines = workspaceAsset.routineState
      ?.map((state) => {
        const version = data.items.find(
          (x: { id: string }) => x.id === state.workspaceRoutineVersionId
        );
        const routine = allRoutines?.find(
          (x) => x.id === state.workspaceRoutineId
        );
        const routineOrder = workspaceAsset.routineOrder?.find(
          (x) => x.workspaceRoutineId === state.workspaceRoutineId
        );

        // Include if the routine is active (either directly or embedded in an active workflow)
        const isDirectlyActive = activeRoutineIds.has(state.workspaceRoutineId);
        const isEmbeddedInActiveWorkflow = data.items.some(
          (v: any) =>
            activeRoutineIds.has(v.workspaceRoutine?.id) && // Workflow is active
            v.config?.pages?.some((page: any) =>
              page.questions?.some(
                (q: any) => q.workspaceRoutineId === state.workspaceRoutineId
              )
            )
        );

        return {
          state:
            isDirectlyActive || isEmbeddedInActiveWorkflow ? state : undefined,
          config: version?.config,
          order: routineOrder?.order ?? routine?.order,
          type: version?.type,
          routines: allRoutines,
        };
      })
      .filter((x) => x.state)
      .sort((a, b) => (a.order ?? 0) - (b?.order ?? 0));

    setSorted(sortedRoutines);
  }, [data, workspaceAsset]);

  const renderHeader = (
    item: SortedRoutineState,
    index: number
  ): ReactNode => {
    const Header =
      RoutineModules[item.type as keyof typeof RoutineModules]?.header;
    if (!Header) {
      logger?.warning(
        `Routine Module not found (${workspaceAsset.id}) ${JSON.stringify(
          item
        )}`
      );
      return null;
    }
    return (
      <Header
        key={`header_${index}`}
        config={item.config}
        currentState={item.state}
        routines={item.routines}
      />
    );
  };

  if (!sorted) {
    return null;
  }

  // TODO: Crop Circle Redesign | remove this legacy scrolling path once the
  // redesign is fully rolled out.
  if (!isCropCircleRedesignEnabled) {
    return (
      <ScrollView horizontal>
        {sorted.map((item, index) => (
          <View key={`view_${index}`} style={styles.header}>
            {renderHeader(item, index)}
          </View>
        ))}
      </ScrollView>
    );
  }

  // Only keep routines we can actually render so the visible/overflow counts
  // stay accurate (a missing module would otherwise occupy a 0-width slot).
  const renderable = sorted.filter(
    (item) => !!RoutineModules[item.type as keyof typeof RoutineModules]?.header
  );

  return <CollapsingPills items={renderable} renderHeader={renderHeader} />;
};

// Lays the routine pills out on a single row (no horizontal scroll). When they
// don't all fit the available width, the overflow is collapsed into a single
// "+N" pill — mirrors the label-overflow treatment in ActivityCard.
const CollapsingPills = ({
  items,
  renderHeader,
}: {
  items: SortedRoutineState[];
  renderHeader: (item: SortedRoutineState, index: number) => ReactNode;
}) => {
  const [containerWidth, setContainerWidth] = useState(0);
  const [itemWidths, setItemWidths] = useState<number[]>([]);
  const itemWidthsRef = useRef<number[]>([]);
  const reportedRef = useRef<Set<number>>(new Set());

  // Re-measure whenever the set of pills changes.
  const itemsKey = useMemo(
    () => items.map((it, i) => `${it.type}-${it.order ?? i}`).join('|'),
    [items]
  );
  useEffect(() => {
    itemWidthsRef.current = [];
    reportedRef.current = new Set();
    setItemWidths([]);
  }, [itemsKey]);

  const measured =
    containerWidth > 0 && itemWidths.length >= items.length && items.length > 0;

  const { visibleCount, overflow } = useMemo(() => {
    if (!measured) {
      return { visibleCount: items.length, overflow: 0 };
    }
    let used = 0;
    let count = 0;
    for (let i = 0; i < items.length; i++) {
      const w = itemWidths[i];
      const gapBefore = i > 0 ? PILL_GAP : 0;
      const remaining = items.length - i - 1;
      // Reserve room for the "+N" pill while there are still items left to place.
      const reserve = remaining > 0 ? OVERFLOW_PILL_WIDTH + PILL_GAP : 0;
      if (used + gapBefore + w + reserve <= containerWidth) {
        used += gapBefore + w;
        count++;
      } else {
        break;
      }
    }
    return { visibleCount: count, overflow: items.length - count };
  }, [measured, items.length, itemWidths, containerWidth]);

  const handleItemLayout = (index: number, e: LayoutChangeEvent) => {
    itemWidthsRef.current[index] = e.nativeEvent.layout.width;
    reportedRef.current.add(index);
    // Complete once every pill has reported, regardless of width (a 0-width
    // pill must not stall measurement and leave the row uncollapsed).
    if (reportedRef.current.size === items.length) {
      setItemWidths([...itemWidthsRef.current]);
    }
  };

  return (
    <View
      style={styles.pillRow}
      onLayout={(e) => setContainerWidth(e.nativeEvent.layout.width)}
    >
      {/* Hidden measurement pass: render each pill off-flow to capture its
          natural width once, then collapse the visible row accordingly. */}
      {!measured && (
        <View style={styles.measureRow} pointerEvents="none">
          {items.map((item, index) => (
            <View
              key={`measure_${index}`}
              style={styles.pill}
              onLayout={(e) => handleItemLayout(index, e)}
            >
              {renderHeader(item, index)}
            </View>
          ))}
        </View>
      )}

      {items.slice(0, visibleCount).map((item, index) => {
        const isLastOverall = index === visibleCount - 1 && overflow === 0;
        return (
          <View
            key={`pill_${index}`}
            style={[styles.pill, isLastOverall && { marginRight: 0 }]}
          >
            {renderHeader(item, index)}
          </View>
        );
      })}
      {overflow > 0 && (
        <View style={[styles.pill, { marginRight: 0 }]}>
          <SubHeaderText>{`+${overflow}`}</SubHeaderText>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  pillRow: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    minWidth: 0,
    overflow: 'hidden',
  },
  measureRow: {
    flexDirection: 'row',
    position: 'absolute',
    opacity: 0,
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: PILL_GAP,
  },
});

const GET_ROUTINE_VERSIONS = `
  query FindWorkspaceRoutineVersions($filter: WorkspaceRoutineVersionFilter) {
    findWorkspaceRoutineVersions(filter: $filter) {
      items {
        config
        id
        type
        workspaceRoutine {
          id
        }
      }
    }
  }
`;
